/* sb-overlay.js — Prefetch + cache overlay HTML so details don't lag behind hero
   + Dawn-safe ATC (FormData + routes.cart_add_url)
   + Refresh Dawn cart sections so cart icon bubble + drawer update
   + Zoom-toggle morph -> X close
   + Product-to-product navigation:
       - chevrons only show at scroll edges
       - MOBILE: vertical swipe on the product image only (up/down = next/prev)
         page scrolling is done outside the image area
       - DESKTOP: wheel/trackpad overscroll requires a “second push” at the edge
       - AUTO-DETECTS the real scroll container (body/info/panel/document)
*/

(function () {
  const PRODUCT_LINK_SELECTOR = 'a[href*="/products/"]:not([data-sb-no-overlay])';
  const CACHE_TTL = 5 * 60 * 1000;
  const MORPH_MS = 220;
  const OVERLAY_QUERY_PARAM = "sbov";
  const SHARE_FEEDBACK_MS = 1400;

  let overlayEl, bodyEl, closeBtnEl, panelEl, shareBtnEl;
  let overlaySocialsSlotEl = null;
  let overlaySocialsRootEl = null;
  let overlaySocialsToggleEl = null;
  let lastFocusedEl = null;
  let previewSrc = null;
  let overlayBaseUrl = "";
  let shareFeedbackTimer = 0;
  let overlayCartDirty = false;

  let morphEl = null;
  let morphAnim = null;

  // Product-to-product nav context (from the page grid)
  let navHandles = [];
  let navIndex = -1;

  // Cleanup so listeners never stack across product swaps
  let cleanupProductNav = null;

  // Global nav lock so fast flicks can't "skip" across products when overlay re-inits
  let navLockUntil = 0;
  let navInFlight = false;

  const htmlCache = new Map();
  const htmlInflight = new Map();

  function now() { return Date.now(); }

  function getHandleFromHref(href) {
    try {
      const url = new URL(href, window.location.origin);
      const parts = url.pathname.split("/").filter(Boolean);
      const idx = parts.indexOf("products");
      if (idx === -1 || !parts[idx + 1]) return null;
      return parts[idx + 1];
    } catch {
      return null;
    }
  }

  function normalizeOverlayHandle(handle) {
    const raw = String(handle || "").trim().toLowerCase();
    if (!raw) return "";
    return /^[a-z0-9-]+$/.test(raw) ? raw : "";
  }

  function getOverlayDeepLinkHandleFromLocation() {
    try {
      const url = new URL(window.location.href);
      return normalizeOverlayHandle(url.searchParams.get(OVERLAY_QUERY_PARAM));
    } catch (_) {
      return "";
    }
  }

  function getUrlWithoutOverlayParam(inputUrl) {
    try {
      const url = new URL(inputUrl || window.location.href, window.location.origin);
      url.searchParams.delete(OVERLAY_QUERY_PARAM);
      return url;
    } catch (_) {
      return new URL(window.location.origin);
    }
  }

  function ensureOverlayBaseUrl() {
    if (!overlayBaseUrl) {
      overlayBaseUrl = getUrlWithoutOverlayParam(window.location.href).toString();
    }
    return overlayBaseUrl;
  }

  function buildOverlayDeepLinkUrl(handle) {
    const cleanHandle = normalizeOverlayHandle(handle);
    const url = getUrlWithoutOverlayParam(ensureOverlayBaseUrl() || window.location.href);
    if (cleanHandle) url.searchParams.set(OVERLAY_QUERY_PARAM, cleanHandle);
    return url.toString();
  }

  function syncOverlayDeepLink(handle) {
    const cleanHandle = normalizeOverlayHandle(handle);
    if (!cleanHandle) return;

    const nextUrl = buildOverlayDeepLinkUrl(cleanHandle);
    if (nextUrl === window.location.href) return;

    try { window.history.replaceState(window.history.state, "", nextUrl); } catch (_) {}
  }

  function clearOverlayDeepLink() {
    const nextUrl = (overlayBaseUrl || getUrlWithoutOverlayParam(window.location.href).toString());
    if (nextUrl && nextUrl !== window.location.href) {
      try { window.history.replaceState(window.history.state, "", nextUrl); } catch (_) {}
    }
    overlayBaseUrl = "";
  }

  function getActiveOverlayHandle() {
    return overlayEl ? normalizeOverlayHandle(overlayEl.getAttribute("data-sb-handle")) : "";
  }

  function resetShareButtonFeedback() {
    if (shareFeedbackTimer) {
      clearTimeout(shareFeedbackTimer);
      shareFeedbackTimer = 0;
    }
    if (!shareBtnEl) return;
    shareBtnEl.classList.remove("is-copied");
    shareBtnEl.setAttribute("aria-label", "Share product link");
    shareBtnEl.setAttribute("title", "Share product link");
  }

  function updateShareButtonState(handle) {
    if (!shareBtnEl) return;
    const cleanHandle = normalizeOverlayHandle(handle);
    shareBtnEl.disabled = !cleanHandle;
    if (!cleanHandle) resetShareButtonFeedback();
  }

  function flashShareCopiedState() {
    if (!shareBtnEl) return;
    resetShareButtonFeedback();
    shareBtnEl.classList.add("is-copied");
    shareBtnEl.setAttribute("aria-label", "Link copied");
    shareBtnEl.setAttribute("title", "Link copied");
    shareFeedbackTimer = window.setTimeout(() => {
      shareFeedbackTimer = 0;
      resetShareButtonFeedback();
    }, SHARE_FEEDBACK_MS);
  }

  async function copyTextToClipboard(text) {
    if (!text) return false;

    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (_) {}

    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      ta.style.pointerEvents = "none";
      ta.style.inset = "0 auto auto -9999px";
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, ta.value.length);
      const ok = document.execCommand("copy");
      ta.remove();
      return !!ok;
    } catch (_) {
      return false;
    }
  }

  async function handleShareClick(event) {
    if (event) event.preventDefault();
    const handle = getActiveOverlayHandle();
    if (!handle) return;

    const url = buildOverlayDeepLinkUrl(handle);
    const titleEl = bodyEl ? bodyEl.querySelector(".sb-ov__title") : null;
    const title = (titleEl && titleEl.textContent ? titleEl.textContent.trim() : "") || document.title || "Product";

    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
    } catch (err) {
      if (err && err.name === "AbortError") return;
    }

    const copied = await copyTextToClipboard(url);
    if (copied) flashShareCopiedState();
  }

  function shouldIgnoreClick(e) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return true;
    if (e.button && e.button !== 0) return true;
    return false;
  }

  function buildNavContextFromClick(activeHandle) {
    const links = Array.from(document.querySelectorAll(PRODUCT_LINK_SELECTOR))
      .filter(a => a && !a.closest(".sb-ov"));

    const handles = [];
    for (const a of links) {
      const h = getHandleFromHref(a.getAttribute("href"));
      if (h && !handles.includes(h)) handles.push(h);
    }

    navHandles = handles;
    navIndex = navHandles.indexOf(activeHandle);
  }

  function syncNavIndexToHandle(handle) {
    if (!handle) return;
    if (navHandles && navHandles.length) {
      const idx = navHandles.indexOf(handle);
      if (idx !== -1) navIndex = idx;
    }
  }

  function canPrev() { return navIndex > 0; }
  function canNext() { return navIndex >= 0 && navIndex < navHandles.length - 1; }

  // Prefetch adjacent products in the nav list so first swipe/wheel doesn’t flash white
  function prefetchAdjacent() {
    if (!navHandles || !navHandles.length || navIndex < 0) return;
    const prev = navHandles[navIndex - 1];
    const next = navHandles[navIndex + 1];
    if (prev) prefetch(prev);
    if (next) prefetch(next);
  }

  function ensureOverlay() {
    if (overlayEl) return;

    overlayEl = document.createElement("div");
    overlayEl.className = "sb-ov";
    overlayEl.innerHTML = `
      <div class="sb-ov__backdrop" data-sb-ov-backdrop></div>
      <div class="sb-ov__panel" role="dialog" aria-modal="true" aria-label="Product details">
        <div class="sb-ov__bar">
          <div class="sb-ov__bar-left" data-sb-ov-socials-slot></div>
          <div class="sb-ov__bar-right">
            <button
              class="sb-ov__sharebtn"
              type="button"
              data-sb-ov-share
              aria-label="Share product link"
              title="Share product link"
              disabled
            >
              <svg class="sb-ov__shareicon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M14 4h6v6" />
                <path d="M20 4 11 13" />
                <path d="M10 6H7.8A2.8 2.8 0 0 0 5 8.8v7.4A2.8 2.8 0 0 0 7.8 19h7.4a2.8 2.8 0 0 0 2.8-2.8V14" />
              </svg>
            </button>
            <button class="sb-ov__xbtn" type="button" data-sb-ov-close aria-label="Close">
              <span class="sb-ov__x" aria-hidden="true"></span>
            </button>
          </div>
        </div>
        <div class="sb-ov__body" data-sb-ov-body></div>
      </div>
    `;
    document.body.appendChild(overlayEl);

    bodyEl = overlayEl.querySelector("[data-sb-ov-body]");
    closeBtnEl = overlayEl.querySelector("[data-sb-ov-close]");
    panelEl = overlayEl.querySelector(".sb-ov__panel");
    shareBtnEl = overlayEl.querySelector("[data-sb-ov-share]");
    overlaySocialsSlotEl = overlayEl.querySelector("[data-sb-ov-socials-slot]");
    ensureOverlaySocials();
    syncOverlayBarHeightVar();

    // flash-proof default
    if (closeBtnEl) {
      closeBtnEl.classList.remove("is-ready");
      closeBtnEl.style.visibility = "hidden";
      closeBtnEl.style.pointerEvents = "none";
    }

    overlayEl.addEventListener("click", (e) => {
      if (e.target && e.target.matches("[data-sb-ov-backdrop]")) close();
    });
    if (shareBtnEl) shareBtnEl.addEventListener("click", handleShareClick);
    closeBtnEl.addEventListener("click", close);

    document.addEventListener("keydown", (e) => {
      if (!overlayEl.classList.contains("is-open")) return;
      if (overlayEl.querySelector("[data-sb-socials].is-open")) return;
      if (e.key === "Escape") close();
    });
  }

  function syncOverlayBarHeightVar() {
    if (!overlayEl) return;
    const bar = overlayEl.querySelector(".sb-ov__bar");
    if (!bar) return;
    const h = Math.ceil(bar.getBoundingClientRect().height || bar.offsetHeight || 65);
    if (h > 0) overlayEl.style.setProperty("--sb-ov-bar-h", `${h}px`);
  }

  function hideOverlaySocialToggle() {
    if (!overlaySocialsToggleEl) return;
    if (overlaySocialsRootEl) {
      overlaySocialsRootEl.classList.remove("is-socials-visible", "is-carry-in-ready");
    }
    overlaySocialsToggleEl.style.visibility = "hidden";
    overlaySocialsToggleEl.style.pointerEvents = "none";
  }

  function openKlaviyoSignupOverlay() {
    try {
      document.dispatchEvent(new CustomEvent("sb:klaviyo-overlay:open"));
      return true;
    } catch (_) {}

    try {
      const trigger = document.querySelector("[data-sb-klaviyo-open]");
      if (trigger && typeof trigger.click === "function") {
        trigger.click();
        return true;
      }
    } catch (_) {}

    return false;
  }

  function showOverlaySocialToggleNow(animate = true) {
    if (!overlaySocialsToggleEl) return;
    overlaySocialsToggleEl.style.visibility = "";
    overlaySocialsToggleEl.style.pointerEvents = "";
    if (overlaySocialsRootEl) {
      overlaySocialsRootEl.classList.add("is-socials-visible");
      if (animate) overlaySocialsRootEl.classList.add("is-carry-in-ready");
      else overlaySocialsRootEl.classList.remove("is-carry-in-ready");
    }
  }

  function resetOverlaySocialMenuState() {
    if (!overlaySocialsRootEl) return;

    try {
      const openToggle = overlaySocialsRootEl.classList.contains("is-open")
        ? overlaySocialsRootEl.querySelector("[data-sb-socials-toggle]")
        : null;
      if (openToggle && typeof openToggle.click === "function") openToggle.click();
    } catch (_) {}

    overlaySocialsRootEl.classList.remove("is-open", "is-carry-in-ready", "is-socials-visible");
    const menu = overlaySocialsRootEl.querySelector("[data-sb-socials-menu]");
    const backdrop = overlaySocialsRootEl.querySelector("[data-sb-socials-backdrop]");
    const items = overlaySocialsRootEl.querySelectorAll("[data-sb-socials-item]");

    if (overlaySocialsToggleEl) {
      overlaySocialsToggleEl.setAttribute("aria-expanded", "false");
      overlaySocialsToggleEl.setAttribute("aria-label", "Open socials menu");
    }
    if (menu) menu.setAttribute("aria-hidden", "true");
    if (backdrop) backdrop.setAttribute("aria-hidden", "true");
    items.forEach((item) => item.setAttribute("tabindex", "-1"));

    try {
      document.documentElement.classList.remove("sb-socials-open");
      document.body.classList.remove("sb-socials-open");
    } catch (_) {}
  }

  function findHeaderSocialsRoot() {
    return document.querySelector("header [data-sb-socials]:not([data-sb-ov-socials])");
  }

  function findHeaderSocialsToggle() {
    const root = findHeaderSocialsRoot();
    return root ? root.querySelector("[data-sb-socials-toggle]") : null;
  }

  function ensureOverlaySocials() {
    if (!overlaySocialsSlotEl) return;
    if (overlaySocialsRootEl && overlaySocialsSlotEl.contains(overlaySocialsRootEl)) return;

    const source = findHeaderSocialsRoot();
    if (!source) return;

    const clone = source.cloneNode(true);
    clone.classList.add("sb-ov__socials");
    clone.setAttribute("data-sb-ov-socials", "true");
    clone.classList.remove("is-open", "is-carry-in-ready", "is-socials-visible");

    const toggle = clone.querySelector("[data-sb-socials-toggle]");
    const menu = clone.querySelector("[data-sb-socials-menu]");
    const backdrop = clone.querySelector("[data-sb-socials-backdrop]");
    const items = clone.querySelectorAll("[data-sb-socials-item]");

    if (toggle && menu) {
      const id = `SbOverlaySocialsMenu-${Math.random().toString(36).slice(2, 10)}`;
      menu.id = id;
      toggle.setAttribute("aria-controls", id);
      toggle.setAttribute("aria-expanded", "false");
      toggle.setAttribute("aria-label", "Open socials menu");
    }
    if (menu) menu.setAttribute("aria-hidden", "true");
    if (backdrop) backdrop.setAttribute("aria-hidden", "true");
    items.forEach((item) => item.setAttribute("tabindex", "-1"));

    overlaySocialsSlotEl.innerHTML = "";
    overlaySocialsSlotEl.appendChild(clone);

    overlaySocialsRootEl = clone;
    overlaySocialsToggleEl = clone.querySelector("[data-sb-socials-toggle]");
  }

  function cleanupMorph() {
    try { if (morphAnim) morphAnim.cancel(); } catch (_) {}
    morphAnim = null;
    if (morphEl) {
      try { morphEl.remove(); } catch (_) {}
      morphEl = null;
    }
  }

  function safeCloseCartNotification() {
    const n =
      document.querySelector("cart-notification") ||
      document.querySelector("#cart-notification") ||
      document.querySelector(".cart-notification");

    if (!n) return;

    try { if (typeof n.close === "function") n.close(); } catch (_) {}
    try {
      n.classList.remove("active", "is-active", "open");
      n.removeAttribute("open");
      n.setAttribute("aria-hidden", "true");
    } catch (_) {}
  }

  async function refreshCartSections() {
    const routes = window.routes || {};
    const cartUrl = routes.cart_url || "/cart";

    const sectionNames = ["cart-icon-bubble", "cart-drawer", "cart-notification"];
    const url = `${cartUrl}?sections=${encodeURIComponent(sectionNames.join(","))}`;

    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) throw new Error("Failed to refresh cart sections");
    const sections = await res.json();

    try {
      const html = sections["cart-icon-bubble"];
      if (html) {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const current = document.querySelector("#cart-icon-bubble");
        const freshSection =
          doc.querySelector("#shopify-section-cart-icon-bubble") ||
          doc.querySelector(".shopify-section");
        const freshAnchor = doc.querySelector("#cart-icon-bubble");

        if (current) {
          if (freshSection) current.innerHTML = freshSection.innerHTML;
          else if (freshAnchor) current.innerHTML = freshAnchor.innerHTML;
        }
      }
    } catch (e) {
      console.warn("[sb-overlay] cart-icon-bubble refresh failed:", e);
    }

    try {
      const html = sections["cart-drawer"];
      if (html) {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const freshDrawer = doc.querySelector("cart-drawer");
        const currentDrawer = document.querySelector("cart-drawer");
        if (freshDrawer && currentDrawer) currentDrawer.replaceWith(freshDrawer);
      }
    } catch (e) {
      console.warn("[sb-overlay] cart-drawer refresh failed:", e);
    }

    try {
      const html = sections["cart-notification"];
      if (html) {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const freshNotif = doc.querySelector("cart-notification") || doc.querySelector("#cart-notification");
        const currentNotif = document.querySelector("cart-notification") || document.querySelector("#cart-notification");
        if (freshNotif && currentNotif) currentNotif.replaceWith(freshNotif);
      }
    } catch (e) {
      console.warn("[sb-overlay] cart-notification refresh failed:", e);
    }
  }

  async function openCartDrawer() {
    const drawer = document.querySelector("cart-drawer");
    if (!drawer) return;
    try {
      if (typeof drawer.open === "function") drawer.open();
      else {
        const cartLink =
          document.querySelector('a[href="/cart"]') ||
          document.querySelector('a[href^="/cart?"]') ||
          document.querySelector('a[href*="/cart"]');
        if (cartLink) cartLink.click();
      }
    } catch (e) {
      console.warn("[sb-overlay] drawer open failed:", e);
    }
  }

  async function addToCart(variantId, quantity) {
    const idStr = String(variantId || "").trim();
    if (!idStr) throw new Error("Missing variant id");

    const fd = new FormData();
    fd.append("id", idStr);
    fd.append("quantity", String(quantity || 1));

    const addUrl =
      (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : "/cart/add.js";

    const res = await fetch(addUrl, {
      method: "POST",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      body: fd,
    });

    if (!res.ok) throw new Error("ATC failed");
    return await res.json();
  }

  function open() {
    ensureOverlay();
    ensureOverlaySocials();
    syncOverlayBarHeightVar();
    const wasAlreadyOpen = !!(overlayEl && overlayEl.classList.contains("is-open"));
    lastFocusedEl = document.activeElement;

    if (closeBtnEl) {
      closeBtnEl.classList.remove("is-ready");
      closeBtnEl.style.visibility = "hidden";
      closeBtnEl.style.pointerEvents = "none";
    }
    resetOverlaySocialMenuState();
    if (!wasAlreadyOpen) hideOverlaySocialToggle();
    else showOverlaySocialToggleNow(false);

    overlayEl.classList.add("is-open");
    updateShareButtonState(getActiveOverlayHandle());
    document.documentElement.style.overflow = "hidden";
    requestAnimationFrame(syncOverlayBarHeightVar);
  }

  function close() {
    if (!overlayEl) return;
    const shouldSyncCart = overlayCartDirty;

    cleanupMorph();
    safeCloseCartNotification();

    try { if (typeof cleanupProductNav === "function") cleanupProductNav(); } catch (_) {}
    cleanupProductNav = null;

    overlayEl.classList.remove("is-open");
    overlayEl.classList.remove("is-loading");
    overlayEl.classList.remove("is-info-ready");
    overlayEl.classList.remove("is-edge-top", "is-edge-bottom");
    overlayEl.style.removeProperty("--sb-ov-preview");
    overlayEl.removeAttribute("data-sb-handle");
    clearOverlayDeepLink();
    updateShareButtonState("");
    document.documentElement.style.overflow = "";

    if (closeBtnEl) {
      closeBtnEl.classList.remove("is-ready");
      closeBtnEl.style.visibility = "hidden";
      closeBtnEl.style.pointerEvents = "none";
    }
    resetOverlaySocialMenuState();
    hideOverlaySocialToggle();

    try {
      document.dispatchEvent(
        new CustomEvent("sb:overlay:closed", { detail: { cartDirty: shouldSyncCart } })
      );
    } catch (_) {}

    if (shouldSyncCart) {
      overlayCartDirty = false;
      refreshCartSections().catch((err) => {
        console.warn("[sb-overlay] close-time cart refresh failed:", err);
      });
    }

    setTimeout(() => {
      if (bodyEl) bodyEl.innerHTML = "";
      previewSrc = null;
      resetShareButtonFeedback();
      if (lastFocusedEl && typeof lastFocusedEl.focus === "function") lastFocusedEl.focus();
    }, 240);
  }

  function capturePreviewFromLink(a) {
    try {
      const img =
        a.querySelector("img") ||
        (a.closest("[data-product-card], .card, .product-card, .grid__item")
          ? a.closest("[data-product-card], .card, .product-card, .grid__item").querySelector("img")
          : null);
      previewSrc = img ? img.currentSrc || img.src || null : null;
    } catch {
      previewSrc = null;
    }
  }

  function setPreviewVar() {
    if (!overlayEl) return;
    if (previewSrc) {
      overlayEl.style.setProperty(
        "--sb-ov-preview",
        `url('${String(previewSrc).replace(/'/g, "\\'")}')`
      );
    } else {
      overlayEl.style.removeProperty("--sb-ov-preview");
    }
  }

  function renderSkeleton() {
    bodyEl.innerHTML = `
      <div class="sb-ov__skeleton">
        <div class="sb-ov__sk-left"></div>
        <div class="sb-ov__sk-right">
          <div class="sb-ov__sk-line"></div>
          <div class="sb-ov__sk-line"></div>
          <div class="sb-ov__sk-line sb-ov__sk-line--short"></div>
          <div class="sb-ov__sk-gap"></div>
          <div class="sb-ov__sk-btn"></div>
          <div class="sb-ov__sk-btn"></div>
        </div>
      </div>
    `;
  }

  function getCachedHTML(handle) {
    const entry = htmlCache.get(handle);
    if (!entry) return null;
    if (now() - entry.t > CACHE_TTL) {
      htmlCache.delete(handle);
      return null;
    }
    return entry.html || null;
  }

  function getPreviewSrcFromHTML(html) {
    if (!html) return null;
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const img =
        doc.querySelector(".sb-ov__img") ||
        doc.querySelector("[data-sb-carousel] img") ||
        doc.querySelector("img");
      if (!img) return null;
      return img.getAttribute("src") || img.currentSrc || img.src || null;
    } catch {
      return null;
    }
  }

  function syncPreviewSrcFromRoot(root) {
    if (!root) return;
    try {
      const img = root.querySelector(".sb-ov__img");
      const src = img ? (img.currentSrc || img.getAttribute("src") || img.src || null) : null;
      if (!src) return;
      previewSrc = src;
      setPreviewVar();
    } catch (_) {}
  }

  async function prefetch(handle) {
    if (!handle) return null;

    const cached = getCachedHTML(handle);
    if (cached) return cached;

    if (htmlInflight.has(handle)) return htmlInflight.get(handle);

    const url = `/products/${handle}?section_id=sb-overlay-pdp`;

    const p = fetch(url, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.text() : null))
      .then((html) => {
        if (html) htmlCache.set(handle, { html, t: now() });
        return html;
      })
      .catch((err) => {
        console.error("[sb-overlay] prefetch failed:", err);
        return null;
      })
      .finally(() => htmlInflight.delete(handle));

    htmlInflight.set(handle, p);
    return p;
  }

  function initCarousel(root) {
    const hero = root.querySelector("[data-sb-carousel]");
    if (!hero) return;

    const track = hero.querySelector("[data-sb-track]");
    const allSlides = Array.from(hero.querySelectorAll("[data-sb-slide]"));
    const prev = hero.querySelector("[data-sb-prev]");
    const next = hero.querySelector("[data-sb-next]");

    if (!track || !allSlides.length) return;

    // Color-based gallery filtering (see updateGalleryForVariant) hides
    // non-matching slides with `display: none` at runtime rather than
    // removing them from the DOM. All index math below is therefore always
    // computed against the currently-VISIBLE slide set, read live each time,
    // so arrow nav and index math stay correct no matter how many slides are
    // hidden/shown after this initial setup runs.
    function getVisibleSlides() {
      return allSlides.filter((s) => s.style.display !== "none");
    }

    function updateArrowVisibility() {
      const visible = getVisibleSlides();
      const show = visible.length > 1;
      if (prev) prev.style.display = show ? "" : "none";
      if (next) next.style.display = show ? "" : "none";
    }

    function getIndex() {
      const visible = getVisibleSlides();
      const w = track.clientWidth || 1;
      const idx = Math.round(track.scrollLeft / w);
      return Math.max(0, Math.min(Math.max(0, visible.length - 1), idx));
    }

    function scrollToIndex(i) {
      const visible = getVisibleSlides();
      if (!visible.length) return;
      const w = track.clientWidth || 1;
      const idx = Math.max(0, Math.min(visible.length - 1, i));
      track.scrollTo({ left: idx * w, behavior: "smooth" });
    }

    if (prev) prev.addEventListener("click", (e) => { e.preventDefault(); scrollToIndex(getIndex() - 1); });
    if (next) next.addEventListener("click", (e) => { e.preventDefault(); scrollToIndex(getIndex() + 1); });

    updateArrowVisibility();

    // Keep prev/next arrow visibility correct whenever the color-based
    // gallery filter toggles slide `style.display` (Black <-> White, etc.).
    try {
      const mo = new MutationObserver(updateArrowVisibility);
      mo.observe(track, { attributes: true, attributeFilter: ["style"], subtree: true });
    } catch (_) {}
  }

  // === KEY FIX: detect the REAL scroll container ===
  function detectScroller(root) {
    const infoCol = root ? root.querySelector(".sb-ov__info") : null;

    function isScrollable(el) {
      try {
        if (!el) return false;
        const sh = el.scrollHeight || 0;
        const ch = el.clientHeight || 0;
        if (sh <= ch + 2) return false;
        const cs = window.getComputedStyle(el);
        const oy = (cs.overflowY || "").toLowerCase();
        return oy === "auto" || oy === "scroll" || oy === "overlay";
      } catch {
        return false;
      }
    }

    if (overlayEl && overlayEl.classList.contains("is-open")) {
      if (isScrollable(bodyEl)) return bodyEl;
      if (isScrollable(infoCol)) return infoCol;
    }

    const candidates = [
      bodyEl,
      infoCol,
      panelEl,
      overlayEl,
      document.scrollingElement || document.documentElement
    ].filter(Boolean);

    function score(el) {
      try {
        const sh = el.scrollHeight || 0;
        const ch = el.clientHeight || 0;
        if (sh <= ch + 2) return 0;

        const cs = el === document.documentElement
          ? { overflowY: "auto" }
          : window.getComputedStyle(el);

        const oy = (cs.overflowY || "").toLowerCase();
        const ok = (oy === "auto" || oy === "scroll" || oy === "overlay");
        return ok ? (sh - ch) : 0;
      } catch {
        return 0;
      }
    }

    let best = candidates[0] || bodyEl;
    let bestScore = -1;
    for (const el of candidates) {
      const s = score(el);
      if (s > bestScore) { bestScore = s; best = el; }
    }

    return best || bodyEl;
  }

  // --- Product nav: edge-aware chevrons + mobile image swipe + desktop wheel ---
  function initProductNav(root) {
    if (!overlayEl) return;

    try { if (typeof cleanupProductNav === "function") cleanupProductNav(); } catch (_) {}
    cleanupProductNav = null;

    const prevBtn = root.querySelector("[data-sb-prod-prev]");
    const nextBtn = root.querySelector("[data-sb-prod-next]");
    const infoCol = root.querySelector(".sb-ov__info");
    const heroEl =
      root.querySelector(".sb-ov__hero") ||
      root.querySelector("[data-sb-carousel]") ||
      root.querySelector(".sb-ov__media");

    let scroller = detectScroller(root);

    // iOS fractional scrollTop + momentum needs a slightly bigger edge tolerance
    const EDGE_PX = 6;

    function getMaxScrollTop(el) {
      const sh = el && el.scrollHeight ? el.scrollHeight : 0;
      const ch = el && el.clientHeight ? el.clientHeight : 0;
      return Math.max(0, sh - ch);
    }

    function getEdges() {
      scroller = detectScroller(root);

      const st = scroller.scrollTop || 0;
      const ch = scroller.clientHeight || 0;
      const sh = scroller.scrollHeight || 0;

      if (sh <= ch + 2) return { top: true, bottom: true };

      return {
        top: st <= EDGE_PX,
        bottom: (st + ch) >= (sh - EDGE_PX)
      };
    }

    // ---- Edge chevrons ----
    let raf = 0;
    function updateEdgeClasses() {
      raf = 0;
      const edges = getEdges();
      const showTop = edges.top && canPrev();
      const showBottom = isMobileOverlayNav() ? canNext() : (edges.bottom && canNext());
      overlayEl.classList.toggle("is-edge-top", !!showTop);
      overlayEl.classList.toggle("is-edge-bottom", !!showBottom);
    }
    function scheduleEdgeUpdate() {
      if (raf) return;
      raf = requestAnimationFrame(updateEdgeClasses);
    }

    // ---- Navigation gating ----
    const INTENT_RATIO = 1.25;
    const MIN_GESTURE_MS = 90;
    const NAV_COOLDOWN_MS = 850;

    const WHEEL_TRIGGER = 160;
    const WHEEL_RESET_MS = 240;

    function isMobileOverlayNav() {
      try { return window.matchMedia("(max-width: 989px)").matches; } catch (_) {}
      return (window.innerWidth || 0) <= 989;
    }

    async function go(delta) {
      const t = Date.now();
      if (t < navLockUntil) return;
      if (navInFlight) return;

      if (!navHandles.length || navIndex < 0) return;
      const next = navIndex + delta;
      if (next < 0 || next >= navHandles.length) return;

      navLockUntil = t + NAV_COOLDOWN_MS;
      navInFlight = true;

      navIndex = next;
      const handle = navHandles[navIndex];
      if (!handle) { navInFlight = false; return; }

      // Pre-warm neighbors again at new index
      prefetchAdjacent();

      // Keep chevrons correct instantly while we load
      scheduleEdgeUpdate();

      // Do the swap
      try {
        await openProductOverlay(handle, { transitionDir: delta });
      } finally {
        navInFlight = false;
      }
    }

    function onPrevClick(e) {
      e.preventDefault();
      e.stopPropagation();
      const edges = getEdges();
      if (!edges.top || !canPrev()) return;
      go(-1);
    }

    function onNextClick(e) {
      e.preventDefault();
      e.stopPropagation();
      if (!canNext()) return;
      if (!isMobileOverlayNav()) {
        const edges = getEdges();
        if (!edges.bottom) return;
      }
      go(+1);
    }

    if (prevBtn) prevBtn.addEventListener("click", onPrevClick);
    if (nextBtn) nextBtn.addEventListener("click", onNextClick);

    // Listen to scroll on MULTIPLE candidates because the "real" scroller can change
    const scrollEls = new Set([bodyEl, panelEl, overlayEl, infoCol, document.scrollingElement].filter(Boolean));
    function onAnyScroll() {
      scheduleEdgeUpdate();
    }
    scrollEls.forEach(el => {
      try { el.addEventListener("scroll", onAnyScroll, { passive: true }); } catch (_) {}
    });

    function onResize() { scheduleEdgeUpdate(); }
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    const mo = new MutationObserver(() => { scheduleEdgeUpdate(); });
    try { mo.observe(root, { childList: true, subtree: true }); } catch (_) {}

    // ==========================
    // MOBILE: Image-only vertical swipe nav
    // ==========================
    let touching = false;
    let tStartX = 0, tStartY = 0, tStartT = 0;
    let peakDy = 0;
    let peakDx = 0;
    let axisLock = ""; // "", "x", "y"

    const heroSwipeEl = heroEl;
    const AXIS_LOCK_PX = 10;
    const HERO_SWIPE_PX = 90;

    function touchStart(e) {
      if (!overlayEl.classList.contains("is-open")) return;
      if (!heroSwipeEl) return;
      if (!e.touches || e.touches.length !== 1) return;
      if (Date.now() < navLockUntil) return;

      touching = true;
      tStartT = Date.now();
      tStartX = e.touches[0].clientX;
      tStartY = e.touches[0].clientY;
      peakDy = 0;
      peakDx = 0;
      axisLock = "";
    }

    function touchMove(e) {
      if (!touching) return;
      if (!e.touches || !e.touches.length) return;

      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;
      const dx = x - tStartX;
      const dy = y - tStartY;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      peakDx = Math.max(peakDx, absDx);
      peakDy = Math.max(peakDy, absDy);

      if (!axisLock && (absDx >= AXIS_LOCK_PX || absDy >= AXIS_LOCK_PX)) {
        axisLock = absDy > absDx ? "y" : "x";
      }

      // On the image area, vertical swipes are reserved for product nav.
      // Prevent overlay/page scrolling once the gesture is clearly vertical.
      if (axisLock === "y") {
        try { e.preventDefault(); } catch (_) {}
      }
    }

    function touchEnd(e) {
      if (!touching) return;
      touching = false;

      if (axisLock !== "y") return; // horizontal image swipes remain for the carousel

      const dt = Date.now() - tStartT;
      if (dt < MIN_GESTURE_MS) return;

      // Must be mostly vertical and long enough
      if (peakDy < peakDx * INTENT_RATIO) return;
      if (peakDy < HERO_SWIPE_PX) return;

      const ct = (e.changedTouches && e.changedTouches[0]) ? e.changedTouches[0] : null;
      if (!ct) return;

      const endDy = ct.clientY - tStartY;

      // Swipe UP on image -> next product
      if (endDy < 0) {
        if (!canNext()) return;
        go(+1);
        return;
      }

      // Swipe DOWN on image -> previous product
      if (endDy > 0) {
        if (!canPrev()) return;
        go(-1);
      }
    }

    function touchCancel() {
      touching = false;
      axisLock = "";
    }

    if (heroSwipeEl) {
      heroSwipeEl.addEventListener("touchstart", touchStart, { passive: true, capture: true });
      heroSwipeEl.addEventListener("touchmove", touchMove, { passive: false, capture: true });
      heroSwipeEl.addEventListener("touchend", touchEnd, { passive: true, capture: true });
      heroSwipeEl.addEventListener("touchcancel", touchCancel, { passive: true, capture: true });
    }

    // ==========================
    // DESKTOP: Wheel overscroll (already “second push” gated)
    // ==========================
    let wheelAcc = 0;
    let wheelTS = 0;
    let wheelArmedDir = 0; // -1 up, +1 down

    function resetWheel() {
      wheelAcc = 0;
      wheelTS = 0;
      wheelArmedDir = 0;
    }

    function onWheel(e) {
      if (!overlayEl.classList.contains("is-open")) return;

      const dy = e.deltaY || 0;
      if (!dy) return;

      const t = Date.now();
      if (t - wheelTS > WHEEL_RESET_MS) resetWheel();
      wheelTS = t;

      const edges = getEdges();

      // Up (prev)
      if (dy < 0) {
        if (!edges.top || !canPrev()) { resetWheel(); return; }

        if (wheelArmedDir !== -1) {
          wheelArmedDir = -1;
          wheelAcc = 0;
          return; // first push arms
        }

        wheelAcc += Math.abs(dy);
        if (wheelAcc >= WHEEL_TRIGGER) {
          resetWheel();
          go(-1);
        }
        return;
      }

      // Down (next)
      if (dy > 0) {
        if (!edges.bottom || !canNext()) { resetWheel(); return; }

        if (wheelArmedDir !== +1) {
          wheelArmedDir = +1;
          wheelAcc = 0;
          return; // first push arms
        }

        wheelAcc += Math.abs(dy);
        if (wheelAcc >= WHEEL_TRIGGER) {
          resetWheel();
          go(+1);
        }
      }
    }

    panelEl.addEventListener("wheel", onWheel, { passive: true });

    scheduleEdgeUpdate();

    cleanupProductNav = function () {
      try {
        if (prevBtn) prevBtn.removeEventListener("click", onPrevClick);
        if (nextBtn) nextBtn.removeEventListener("click", onNextClick);
      } catch (_) {}

      scrollEls.forEach(el => {
        try { el.removeEventListener("scroll", onAnyScroll); } catch (_) {}
      });

      try { window.removeEventListener("resize", onResize); } catch (_) {}
      try { window.removeEventListener("orientationchange", onResize); } catch (_) {}

      try { mo.disconnect(); } catch (_) {}

      try { if (heroSwipeEl) heroSwipeEl.removeEventListener("touchstart", touchStart, true); } catch (_) {}
      try { if (heroSwipeEl) heroSwipeEl.removeEventListener("touchmove", touchMove, true); } catch (_) {}
      try { if (heroSwipeEl) heroSwipeEl.removeEventListener("touchend", touchEnd, true); } catch (_) {}
      try { if (heroSwipeEl) heroSwipeEl.removeEventListener("touchcancel", touchCancel, true); } catch (_) {}

      try { panelEl.removeEventListener("wheel", onWheel); } catch (_) {}
    };
  }

  function findZoomToggle() {
    return overlayEl ? overlayEl.querySelector("[data-sb-zoom-toggle]") : null;
  }

  function showRealCloseNow() {
    if (!closeBtnEl) return;
    closeBtnEl.style.visibility = "";
    closeBtnEl.style.pointerEvents = "";
    closeBtnEl.classList.add("is-ready");
  }

  function getPanelTranslateXY() {
    if (!panelEl) return { x: 0, y: 0 };

    const t = window.getComputedStyle(panelEl).transform;
    if (!t || t === "none") return { x: 0, y: 0 };

    const m = t.match(/matrix\(([^)]+)\)/);
    if (!m) return { x: 0, y: 0 };
    const parts = m[1].split(",").map(s => parseFloat(s.trim()));
    return {
      x: parts.length >= 6 ? parts[4] : 0,
      y: parts.length >= 6 ? parts[5] : 0
    };
  }

  function startZoomMorphToClose() {
    cleanupMorph();
    if (!overlayEl || !closeBtnEl) return;

    const zoomBtn = findZoomToggle();
    if (!zoomBtn) {
      showRealCloseNow();
      try { closeBtnEl.focus(); } catch (_) {}
      return;
    }

    const from = zoomBtn.getBoundingClientRect();
    const toRaw = closeBtnEl.getBoundingClientRect();
    const tr = getPanelTranslateXY();
    const to = {
      left: toRaw.left - tr.x,
      top: toRaw.top - tr.y,
      width: toRaw.width,
      height: toRaw.height,
    };

    morphEl = document.createElement("button");
    morphEl.type = "button";
    morphEl.className = "sb-ov__morphbtn";
    morphEl.setAttribute("aria-hidden", "true");
    morphEl.tabIndex = -1;
    morphEl.innerHTML = `<span class="sb-ov__plus" aria-hidden="true"></span>`;

    morphEl.style.left = `${from.left}px`;
    morphEl.style.top = `${from.top}px`;
    morphEl.style.width = `${from.width}px`;
    morphEl.style.height = `${from.height}px`;

    document.body.appendChild(morphEl);

    const dx = (to.left + to.width / 2) - (from.left + from.width / 2);
    const dy = (to.top + to.height / 2) - (from.top + from.height / 2);
    const scaleX = to.width / Math.max(1, from.width);
    const scaleY = to.height / Math.max(1, from.height);

    morphAnim = morphEl.animate(
      [
        { transform: "translate(0px, 0px) rotate(0deg) scale(1, 1)" },
        { transform: `translate(${dx}px, ${dy}px) rotate(45deg) scale(${scaleX}, ${scaleY})` },
      ],
      {
        duration: MORPH_MS,
        easing: "cubic-bezier(0.2, 0.8, 0.2, 1)",
        fill: "forwards",
      }
    );

    morphAnim.onfinish = () => {
      showRealCloseNow();
      requestAnimationFrame(() => cleanupMorph());
      requestAnimationFrame(() => {
        try { closeBtnEl.focus(); } catch (_) {}
      });
    };

    morphAnim.oncancel = () => {
      showRealCloseNow();
      requestAnimationFrame(() => cleanupMorph());
    };
  }

  function initInjectedPDP(root, transitionDir = 0) {
    overlayEl.classList.add("is-info-ready");

    // Always warm neighbors as soon as PDP is in place (fixes first-nav white flash)
    prefetchAdjacent();

    try {
      const hero = root.querySelector(".sb-ov__hero");
      const firstImg = root.querySelector(".sb-ov__img");

      let previewImgEl = null;
      if (hero && previewSrc) {
        previewImgEl = document.createElement("img");
        previewImgEl.className = "sb-ov__preview-img";
        if (transitionDir > 0) previewImgEl.classList.add("is-nav-next");
        else if (transitionDir < 0) previewImgEl.classList.add("is-nav-prev");
        previewImgEl.alt = "";
        previewImgEl.decoding = "async";
        previewImgEl.loading = "eager";
        previewImgEl.src = previewSrc;
        hero.appendChild(previewImgEl);
      }

      // Sync after mounting the preview overlay so nav transitions can use the
      // outgoing image while still updating previewSrc for the next swap.
      syncPreviewSrcFromRoot(root);

      let previewRemoved = false;
      const removePreview = () => {
        if (previewRemoved) return;
        previewRemoved = true;

        overlayEl.classList.remove("is-loading");

        if (!previewImgEl) return;

        let cleaned = false;
        const cleanup = () => {
          if (cleaned) return;
          cleaned = true;
          try { previewImgEl.remove(); } catch (_) {}
        };

        // Smoothly hand off from the placeholder preview to the loaded image.
        const onEnd = (evt) => {
          if (!evt || evt.target !== previewImgEl) return;
          if (evt.propertyName !== "transform") return;
          cleanup();
        };
        try { previewImgEl.addEventListener("transitionend", onEnd); } catch (_) {}
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            try {
              if (!previewImgEl.isConnected) return cleanup();
              previewImgEl.classList.add("is-fading-out");
            } catch (_) {
              cleanup();
            }
          });
        });
        setTimeout(cleanup, 620);
      };

      // If the image never loads in time, still clear loading state/preview safely.
      const removePreviewFallback = () => {
        if (previewRemoved) return;
        previewRemoved = true;
        if (previewImgEl) { try { previewImgEl.remove(); } catch (_) {} }
        overlayEl.classList.remove("is-loading");
      };

      if (firstImg) {
        if (firstImg.complete && firstImg.naturalWidth > 0) removePreview();
        else {
          firstImg.addEventListener("load", removePreview, { once: true });
          setTimeout(removePreviewFallback, 900);
        }
      } else {
        removePreviewFallback();
      }
    } catch (_) {
      overlayEl.classList.remove("is-loading");
    }

    initCarousel(root);
    initProductNav(root);

    // Variants / ATC (unchanged)
    const jsonEl = root.querySelector("[data-sb-product-json]");
    const variantInput = root.querySelector("[data-sb-variant-id]");
    const atcBtn = root.querySelector("[data-sb-atc]") || root.querySelector(".sb-ov__atc");
    const buyNowBtn = root.querySelector("[data-sb-buy-now]") || root.querySelector(".sb-ov__buy");
    const optionBtns = root.querySelectorAll("[data-sb-option]");

    let product = null;
    try { product = jsonEl ? JSON.parse(jsonEl.textContent) : null; } catch { product = null; }

    function getSelectedOptions() {
      const selected = [];
      const groups = new Map();
      optionBtns.forEach((b) => {
        const idx = Number(b.getAttribute("data-option-index"));
        if (!groups.has(idx)) groups.set(idx, []);
        groups.get(idx).push(b);
      });
      const maxIndex = Math.max(-1, ...groups.keys());
      for (let i = 0; i <= maxIndex; i++) {
        const group = groups.get(i) || [];
        const active = group.find((b) => b.classList.contains("is-active"));
        selected[i] = active ? active.getAttribute("data-value") : null;
      }
      return selected;
    }

    function findVariantByOptions(opts) {
      if (!product || !product.variants) return null;
      return product.variants.find((v) =>
        (v.options || []).every((val, i) => val === opts[i])
      ) || null;
    }

    // Generic size-chart-per-color support.
    // Works for ANY product: finds a "Color"/"Colour" option (by name, case
    // insensitive) in product.options, figures out its index, and — on every
    // variant change — shows only the [data-sb-size-chart-card] whose
    // data-color attribute matches the currently selected color value.
    // If the product has no Color option, or no size-chart cards at all,
    // this is a complete no-op (no crash, nothing toggled).
    function getOptionName(opt) {
      // product.options from the Shopify Liquid `| json` drop is typically
      // an array of plain strings (e.g. ["Size", "Color"]), but guard for an
      // array of objects with a `.name` too, just in case.
      if (opt == null) return "";
      if (typeof opt === "string") return opt;
      if (typeof opt === "object" && typeof opt.name === "string") return opt.name;
      return "";
    }

    function findColorOptionIndex() {
      if (!product || !Array.isArray(product.options)) return -1;
      for (let i = 0; i < product.options.length; i++) {
        const name = getOptionName(product.options[i]).toLowerCase();
        if (name === "color" || name === "colour") return i;
      }
      return -1;
    }

    const sizeChartCards = root.querySelectorAll("[data-sb-size-chart-card]");
    const colorOptionIndex = findColorOptionIndex();

    // Color -> gallery filtering.
    //
    // Build a map of colorValue -> Set(mediaId) ONCE per PDP render, by
    // walking every variant and recording which single image
    // (variant.featured_image.id, a standard Liquid/variant-JSON field) is
    // linked to it, keyed by that variant's Color/Colour option value. This
    // requires zero extra Liquid/markup: `data-sb-product-json` already
    // embeds `featured_image` per variant (same field this file's original
    // updateGalleryForVariant already relied on to scroll to a single
    // matching slide), and each rendered gallery slide already carries
    // `data-media-id="{{ img.id }}"` (see sections/sb-overlay-pdp.liquid),
    // which uses the exact same id-space as `featured_image.id`.
    //
    // If a product has no Color/Colour option (colorOptionIndex === -1) this
    // map is intentionally left empty, and updateGalleryForVariant below
    // falls through to the original single-slide-scroll behavior with the
    // full gallery always visible: 100% no-op for every non-Color product.
    function buildColorMediaMap() {
      const map = new Map();
      if (colorOptionIndex < 0) return map;
      if (!product || !Array.isArray(product.variants)) return map;

      product.variants.forEach((v) => {
        if (!v || !Array.isArray(v.options)) return;
        const color = v.options[colorOptionIndex];
        if (color == null) return;

        const mediaId =
          v.featured_image && v.featured_image.id != null
            ? String(v.featured_image.id)
            : null;
        if (!mediaId) return;

        if (!map.has(color)) map.set(color, new Set());
        map.get(color).add(mediaId);
      });

      return map;
    }

    const colorMediaMap = buildColorMediaMap();
    let lastAppliedGalleryColor; // undefined until first filter application

    function updateSizeChartForColor(match) {
      if (!sizeChartCards.length) return; // no size chart on this product/theme state
      if (colorOptionIndex < 0) return; // no Color option: leave chart(s) exactly as rendered

      const selectedColor = match && Array.isArray(match.options)
        ? match.options[colorOptionIndex]
        : null;
      if (selectedColor == null) return;

      sizeChartCards.forEach((card) => {
        const cardColor = card.getAttribute("data-color");
        if (cardColor == null) return; // card has no data-color (single-chart product): leave alone
        if (cardColor === selectedColor) {
          card.hidden = false;
        } else {
          card.hidden = true;
        }
      });
    }

    // Generic variant-aware gallery filtering/swap.
    //
    // Case 1 — product HAS a Color/Colour option AND we successfully built a
    // colorValue -> mediaId-set map (colorMediaMap, above) for it: hide every
    // gallery slide whose image isn't tagged for the currently-selected
    // color, and show only the ones that are (e.g. selecting Black on
    // POV TEE shows only the 4 Black-linked photos; selecting White shows
    // only the 1 White-linked photo). Slides are toggled with
    // `display: none` (not removed from the DOM), which is exactly what
    // `.sb-ov__slide { flex: 0 0 100% }` needs to keep the scroll-snap
    // carousel's layout/track width correct with no gaps. Whenever the
    // selected color actually CHANGES (not just the size), the carousel is
    // reset to the first now-visible slide so it never sits scrolled to a
    // now-hidden position.
    //
    // Case 2 — fallback for every other product (no Color option, or a
    // Color option whose variants don't carry per-variant images): make sure
    // every slide is visible (undoing any filter from a prior state) and
    // fall back to the original behavior — scroll the existing carousel to
    // whichever single slide matches the selected variant's own
    // featured_image, if any. This is the exact pre-existing behavior and is
    // a complete no-op for products where match.featured_image is null or
    // doesn't match a rendered slide.
    function updateGalleryForVariant(match) {
      try {
        const hero = root.querySelector("[data-sb-carousel]");
        const track = hero && hero.querySelector("[data-sb-track]");
        if (!hero || !track) return;
        const slides = Array.from(hero.querySelectorAll("[data-sb-slide]"));
        if (!slides.length) return;

        if (colorMediaMap.size && match && Array.isArray(match.options)) {
          const selectedColor = match.options[colorOptionIndex];
          const idsForColor = selectedColor != null ? colorMediaMap.get(selectedColor) : null;

          if (idsForColor && idsForColor.size) {
            const colorChanged = selectedColor !== lastAppliedGalleryColor;
            let hasVisible = false;

            slides.forEach((s) => {
              const mid = s.getAttribute("data-media-id");
              const isMatch = !!mid && idsForColor.has(mid);
              s.style.display = isMatch ? "" : "none";
              if (isMatch) hasVisible = true;
            });

            lastAppliedGalleryColor = selectedColor;

            // IMPORTANT: because .sb-ov__slide { flex: 0 0 100% } and hidden
            // slides are `display: none` (removed from flex flow entirely,
            // not just visually), the first VISIBLE slide is ALWAYS at
            // physical scroll offset 0 in the track's content after
            // filtering — regardless of that slide's index in the original
            // unfiltered slide list. So resetting to `left: 0` is correct
            // here, not `firstOriginalIndex * slideWidth` (which would land
            // on the wrong slide/a gap once earlier slides are hidden).
            if (colorChanged && hasVisible) {
              track.scrollTo({ left: 0, behavior: "auto" });
            }

            return; // color filtering applied; skip legacy single-slide scroll below
          }
        }

        // Fallback path: ensure nothing is left hidden from a stale filter,
        // then behave exactly like the original single-slide scroll.
        slides.forEach((s) => { s.style.display = ""; });

        if (!match || !match.featured_image || match.featured_image.id == null) return;

        const targetId = String(match.featured_image.id);
        const targetIndex = slides.findIndex(
          (s) => s.getAttribute("data-media-id") === targetId
        );
        if (targetIndex < 0) return; // no matching slide rendered: safe no-op

        const w = track.clientWidth || 1;
        const currentIndex = Math.round(track.scrollLeft / w);
        if (currentIndex === targetIndex) return; // already showing it

        track.scrollTo({ left: targetIndex * w, behavior: "smooth" });
      } catch (_) {
        // Never let gallery swapping break variant selection / ATC.
      }
    }

    function updateVariant() {
      if (!product) return;
      const match = findVariantByOptions(getSelectedOptions());
      if (!match) return;

      if (variantInput) variantInput.value = String(match.id);

      const priceEl = root.querySelector(".sb-ov__price");
      if (priceEl && typeof match.price === "number") {
        priceEl.textContent = `$${(match.price / 100).toFixed(2)}`;
      }

      const soldOut = !match.available;
      if (atcBtn) {
        atcBtn.disabled = false;
        atcBtn.textContent = soldOut ? "Access updates" : "Add to cart";
      }
      if (buyNowBtn) {
        buyNowBtn.disabled = soldOut;
        buyNowBtn.textContent = soldOut ? "Sold out" : "get one";
      }

      try {
        updateSizeChartForColor(match);
      } catch (_) {
        // Never let size-chart toggling break variant selection / ATC.
      }

      updateGalleryForVariant(match);
    }

    optionBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        const idx = btn.getAttribute("data-option-index");
        optionBtns.forEach((b) => {
          if (b.getAttribute("data-option-index") === idx) {
            b.classList.remove("is-active");
            b.setAttribute("aria-pressed", "false");
          }
        });
        btn.classList.add("is-active");
        btn.setAttribute("aria-pressed", "true");
        updateVariant();
      });
    });

    updateVariant();

    const form = root.querySelector("[data-sb-atc-form]");
    if (form) {
      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        const id = variantInput ? variantInput.value : "";
        if (!String(id).trim()) return;

        let selectedVariant = null;
        try {
          selectedVariant = product && Array.isArray(product.variants)
            ? product.variants.find((v) => String(v.id) === String(id)) || null
            : null;
        } catch (_) {
          selectedVariant = null;
        }

        if (selectedVariant && !selectedVariant.available) {
          openKlaviyoSignupOverlay();
          return;
        }

        const prevText = atcBtn ? atcBtn.textContent : "Add to cart";

        try {
          if (atcBtn) {
            atcBtn.disabled = true;
            atcBtn.textContent = "Adding…";
          }

          await addToCart(id, 1);
          overlayCartDirty = true;
          await refreshCartSections();
          await openCartDrawer();
          safeCloseCartNotification();
        } catch (err) {
          console.error("[sb-overlay] add to cart failed:", err);
          alert("Could not add to cart. Try again.");
        } finally {
          if (atcBtn) {
            atcBtn.disabled = false;
            atcBtn.textContent = prevText || "Add to cart";
          }
        }
      });
    }

    if (buyNowBtn) {
      buyNowBtn.addEventListener("click", async () => {
        const id = variantInput ? variantInput.value : "";
        if (!String(id).trim()) return;

        const prev = buyNowBtn.textContent || "get one";

        try {
          buyNowBtn.disabled = true;
          buyNowBtn.textContent = "Loading…";

          await addToCart(id, 1);
          await refreshCartSections();
          window.location.href = "/checkout";
        } catch (err) {
          console.error("[sb-overlay] buy now failed:", err);
          alert("Could not start checkout. Try again.");
        } finally {
          buyNowBtn.disabled = false;
          buyNowBtn.textContent = prev;
        }
      });
    }
  }

  async function openProductOverlay(handle, options = {}) {
    ensureOverlay();
    const transitionDir = Number(options.transitionDir || 0);

    // Keep navIndex correct even if something external calls openProductOverlay
    syncNavIndexToHandle(handle);

    // Prefetch neighbors immediately (restores desktop no-flash on first nav)
    prefetchAdjacent();

    const previousHandle = overlayEl.getAttribute("data-sb-handle") || "";
    const wasOpen = overlayEl.classList.contains("is-open");
    const isNavSwap = !!(transitionDir && wasOpen && handle && handle !== previousHandle);
    overlayEl.setAttribute("data-sb-handle", handle || "");
    syncOverlayDeepLink(handle);
    updateShareButtonState(handle);

    const cachedHTML = getCachedHTML(handle);
    const cachedPreviewSrc = getPreviewSrcFromHTML(cachedHTML);
    if (isNavSwap) {
      // Keep the currently visible product image as the outgoing transition layer.
      // If it isn't available, fall back to the target cached preview.
      if (!previewSrc && cachedPreviewSrc) previewSrc = cachedPreviewSrc;
    } else if (cachedPreviewSrc) {
      previewSrc = cachedPreviewSrc;
    } else if (wasOpen && handle && handle !== previousHandle) {
      // Avoid flashing the prior product image if the target preview isn't ready yet.
      previewSrc = null;
    }
    setPreviewVar();

    const htmlPromise = cachedHTML ? Promise.resolve(cachedHTML) : prefetch(handle);

    open();
    overlayEl.classList.add("is-loading");
    overlayEl.classList.remove("is-info-ready");

    renderSkeleton();

    requestAnimationFrame(() => {
      if (!wasOpen) {
        showOverlaySocialToggleNow(true);
      } else {
        showOverlaySocialToggleNow(false);
      }
      try { startZoomMorphToClose(); } catch (_) { showRealCloseNow(); }
    });

    if (cachedHTML) {
      bodyEl.innerHTML = cachedHTML;
      const root = bodyEl.querySelector("[data-sb-ov-pdp]");
      if (root) initInjectedPDP(root, transitionDir);
      else overlayEl.classList.remove("is-loading");
      return;
    }

    const html = await htmlPromise;
    if (!html) {
      overlayEl.classList.remove("is-loading");
      bodyEl.innerHTML = `<div style="padding:18px; font-size:14px;">Could not load product.</div>`;
      showRealCloseNow();
      return;
    }

    bodyEl.innerHTML = html;
    const root = bodyEl.querySelector("[data-sb-ov-pdp]");
    if (root) initInjectedPDP(root, transitionDir);
    else overlayEl.classList.remove("is-loading");
  }

  function prefetchFromEventTarget(target) {
    const a = target && target.closest ? target.closest(PRODUCT_LINK_SELECTOR) : null;
    if (!a) return;
    const href = a.getAttribute("href");
    const handle = href ? getHandleFromHref(href) : null;
    if (!handle) return;
    prefetch(handle);
  }

  document.addEventListener("mouseover", (e) => {
    prefetchFromEventTarget(e.target);
  }, { passive: true });

  document.addEventListener("focusin", (e) => {
    prefetchFromEventTarget(e.target);
  });

  document.addEventListener("touchstart", (e) => {
    prefetchFromEventTarget(e.target);
  }, { passive: true });

  document.addEventListener(
    "click",
    (e) => {
      if (shouldIgnoreClick(e)) return;
      if (e.target && e.target.closest && e.target.closest("[data-sb-no-overlay]")) return;

      const a = e.target && e.target.closest ? e.target.closest(PRODUCT_LINK_SELECTOR) : null;
      if (!a) return;
      if (a.closest(".sb-ov")) return;

      const href = a.getAttribute("href");
      const handle = href ? getHandleFromHref(href) : null;
      if (!handle) return;

      capturePreviewFromLink(a);

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();

      buildNavContextFromClick(handle);
      openProductOverlay(handle);
    },
    true
  );

  function maybeOpenOverlayFromDeepLink() {
    const handle = getOverlayDeepLinkHandleFromLocation();
    if (!handle) return;
    buildNavContextFromClick(handle);
    openProductOverlay(handle).catch((err) => {
      console.error("[sb-overlay] deep-link open failed:", err);
    });
  }

  function bootOverlay() {
    ensureOverlay();
    maybeOpenOverlayFromDeepLink();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootOverlay, { once: true });
  } else {
    bootOverlay();
  }
})();
